import React, { useState } from 'react';
import { Product, ProductVariant } from '../types';
import { useCart } from '../context/CartContext';
import { X, ZoomIn, ShoppingCart, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useLocation } from 'wouter';
import toast from 'react-hot-toast';

interface QuickViewModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenQuickBuy?: (product: Product) => void;
}

export default function QuickViewModal({ product, isOpen, onClose, onOpenQuickBuy }: QuickViewModalProps) {
  const [, setLocation] = useLocation();
  const { addToCart } = useCart();

  if (!isOpen || !product) return null;

  const defaultVariants: ProductVariant[] = [
    { weight: '100g', price: product?.price || 58, costPrice: 0, image: product?.image || '' },
    { weight: '250g', price: Math.round((product?.price || 58) * 2.3), costPrice: 0, image: product?.image || '' },
  ];
  const variants = product.variants && product.variants.length > 0 ? product.variants : defaultVariants;
  const [selectedWeight, setSelectedWeight] = useState(variants[0]?.weight || '100g');
  const currentVariant = variants.find(v => v.weight === selectedWeight) || variants[0];
  const displayPrice = currentVariant ? currentVariant.price : product.price;
  const isOutOfStock = product.isOutOfStock === true || product.stock === 0;

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    addToCart(product, selectedWeight, 1, displayPrice);
    toast.success(`Added ${product.name} to cart!`, {
      icon: '🛒',
      duration: 2500,
      style: { background: '#2B2B2B', color: '#FFF' }
    });
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-[2px] cursor-pointer"
        />

        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl z-50 overflow-hidden border border-[#E5E0D8]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 border border-gray-300 flex items-center justify-center text-gray-700 hover:text-black hover:border-black transition-colors"
          >
            <X className="w-4 h-4 stroke-[2.2]" />
          </button>

          <div className="p-6">
            <div className="aspect-square w-full max-w-[280px] mx-auto bg-white flex items-center justify-center p-4 mb-4">
              <img
                src={product.image}
                alt={product.name}
                referrerPolicy="no-referrer"
                className="max-h-full max-w-full object-contain"
              />
            </div>

            <div className="text-center mb-4">
              <h2 className="text-lg font-bold text-[#1F1F1F]">{product.name}</h2>
              <p className="text-xs text-gray-500 uppercase tracking-widest mt-0.5">{product.category}</p>
              <div className="text-xl font-extrabold text-[#1F1F1F] mt-2">
                Rs. {displayPrice.toFixed(2)}
              </div>
            </div>

            {product.description && (
              <p className="text-xs text-gray-600 text-center line-clamp-2 mb-4 px-4">
                {product.description}
              </p>
            )}

            {/* Variants */}
            <div className="flex justify-center gap-2 mb-5">
              {variants.map(v => (
                <button
                  key={v.weight}
                  onClick={() => setSelectedWeight(v.weight)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-sm ${
                    selectedWeight === v.weight
                      ? 'variant-box-active'
                      : 'variant-box-inactive'
                  }`}
                >
                  {v.weight}
                </button>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                className="flex-1 py-3 px-4 rounded font-bold text-xs uppercase tracking-wider btn-terracotta bg-[#D97736] hover:bg-[#C85A17] text-white flex items-center justify-center gap-2 shadow-sm"
              >
                <ShoppingCart className="w-4 h-4" />
                <span>{isOutOfStock ? 'Sold out' : 'Add to cart'}</span>
              </button>
              <button
                onClick={() => {
                  onClose();
                  setLocation(`/product/${product.id}`);
                }}
                className="py-3 px-4 rounded border border-gray-300 hover:border-black font-bold text-xs uppercase tracking-wider text-gray-700 flex items-center gap-1.5 transition-colors"
              >
                <span>Details</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
